import { useMemo, useState } from 'react'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/Badge'
import { Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { BonusIndicador, IndicadorDados } from './types'
import type { FieldPermissions } from '@/lib/bonificacao/permissions'
import { IndicatorEvidenceUpload } from './IndicatorEvidenceUpload'
import { IndicatorEvidenceList } from './IndicatorEvidenceList'

export interface IndicatorRowProps {
  indicador: BonusIndicador
  dados: IndicadorDados | undefined
  assessmentId: string
  assessmentStatus: string
  permissions: FieldPermissions
  onUpdate: (indicadorId: string, patch: Partial<IndicadorDados>) => void
  isSaving?: boolean
  /** When true, valor and comprovado are read-only with auto-calculated values */
  isAutoCalculated?: boolean
  /** Helper text shown under auto-calculated indicators (e.g., source data breakdown) */
  autoCalcHelperText?: string
}

const DEFAULT_DADOS: IndicadorDados = {
  valor: null,
  comprovado: false,
  rhValidado: false,
  evidencia: '',
  evidenceFiles: [],
  rhComentario: '',
}

function calcAtingimento(
  indicador: BonusIndicador,
  dados: IndicadorDados
): number {
  if (indicador.binario) {
    return dados.comprovado ? 100 : 0
  }
  const valor = dados.valor ?? 0
  if (indicador.meta <= 0) return 0
  return Math.min((valor / indicador.meta) * 100, indicador.teto ?? 100)
}

function getAtingimentoColor(pct: number): string {
  if (pct >= 100) return 'text-success'
  if (pct >= 70) return 'text-warning'
  return 'text-brand-gray'
}

export function IndicatorRow({
  indicador,
  dados: rawDados,
  assessmentId,
  permissions,
  onUpdate,
  isSaving,
  isAutoCalculated,
  autoCalcHelperText,
}: IndicatorRowProps) {
  const dados = rawDados ?? DEFAULT_DADOS
  const [showRationale, setShowRationale] = useState(false)

  // Parse rationale JSON if present
  const rationale = useMemo(() => {
    if (!dados?._rationale) return null
    try {
      return JSON.parse(dados._rationale) as {
        title: string
        items: { label: string; value: string }[]
        details?: { label: string; value: string }[]
      }
    } catch {
      return null
    }
  }, [dados?._rationale])

  const atingimento = useMemo(
    () => calcAtingimento(indicador, dados),
    [indicador, dados]
  )

  const atingimentoColor = getAtingimentoColor(atingimento)

  const showRhComment =
    permissions.rhComentario || (dados.rhComentario && dados.rhComentario.length > 0)

  return (
    <div
      className="space-y-2 py-3 border-b border-brand-gray/10 last:border-b-0"
      data-testid={`indicator-row-${indicador.id}`}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Name — show _displayName from backend if present (per-director OKR objective) */}
        <span className="text-sm text-white flex-1 min-w-[120px]">
          {dados?._displayName || indicador.nome}
          {isAutoCalculated && (
            <>
              <Badge variant="outline" className="ml-2 text-[10px] text-brand-primary border-brand-primary/30">
                Calculado via Monutrack
              </Badge>
              {rationale && (
                <button
                  type="button"
                  onClick={() => setShowRationale(true)}
                  className="ml-1 text-brand-gray hover:text-white inline-flex transition-colors"
                  title="Ver detalhes do cálculo"
                >
                  <Info className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </span>

        {/* Peso badge */}
        <Badge variant="outline" className="text-xs">
          {indicador.peso}
        </Badge>

        {/* Meta */}
        <span className="text-xs text-brand-gray whitespace-nowrap">
          Meta: {indicador.meta}{indicador.unidade === '%' ? '%' : ` ${indicador.unidade}`}
        </span>

        {/* Valor input — hidden for binary indicators, disabled for auto-calculated */}
        {!indicador.binario && (
          <Input
            type="number"
            className="w-20 h-8 text-sm"
            placeholder="—"
            value={dados.valor ?? ''}
            disabled={!permissions.valor || isAutoCalculated}
            data-testid={`indicator-valor-${indicador.id}`}
            onChange={(e) => {
              if (isAutoCalculated) return
              const raw = e.target.value
              const valor = raw === '' ? null : parseFloat(raw)
              onUpdate(indicador.id, { valor })
            }}
          />
        )}

        {/* Atingimento */}
        <span
          className={`text-sm font-semibold w-16 text-right ${atingimentoColor}`}
          data-testid={`indicator-atingimento-${indicador.id}`}
        >
          {atingimento.toFixed(0)}%
        </span>

        {/* Comprovado switch */}
        <div className="flex items-center gap-1">
          <Switch
            checked={dados.comprovado}
            onCheckedChange={(checked) => {
              if (isAutoCalculated) return
              onUpdate(indicador.id, { comprovado: checked })
            }}
            disabled={!permissions.comprovado || isAutoCalculated}
            data-testid={`indicator-comprovado-${indicador.id}`}
          />
          <span className="text-xs text-brand-gray">Comprovado</span>
        </div>

        {/* RH Validado switch — hidden when permission is false (AC3: only visible at validacao_rh) */}
        {permissions.rhValidado && (
          <div className="flex items-center gap-1">
            <Switch
              checked={dados.rhValidado}
              onCheckedChange={(checked) =>
                onUpdate(indicador.id, { rhValidado: checked })
              }
              disabled={!permissions.rhValidado}
              data-testid={`indicator-rh-validado-${indicador.id}`}
            />
            <span className="text-xs text-brand-gray">Bônus</span>
          </div>
        )}

        {/* Save indicator */}
        {isSaving && (
          <span className="text-xs text-brand-gray animate-pulse">Salvando...</span>
        )}
      </div>

      {/* Evidence textarea + upload */}
      <div className="flex items-start gap-2 pl-0">
        <Textarea
          className="text-sm !min-h-0 max-h-[120px] flex-1 !resize-none"
          placeholder="Evidência..."
          rows={1}
          value={dados.evidencia ?? ''}
          disabled={!permissions.evidencia}
          data-testid={`indicator-evidencia-${indicador.id}`}
          onChange={(e) =>
            onUpdate(indicador.id, { evidencia: e.target.value })
          }
        />
        <IndicatorEvidenceUpload
          assessmentId={assessmentId}
          indicadorId={indicador.id}
          canUpload={permissions.evidenceUpload}
        />
      </div>

      {/* Evidence file list */}
      <IndicatorEvidenceList
        assessmentId={assessmentId}
        indicadorId={indicador.id}
        files={dados.evidenceFiles ?? []}
        canDelete={permissions.evidenceUpload}
      />

      {/* Auto-calc helper text */}
      {isAutoCalculated && autoCalcHelperText && (
        <p className="text-xs text-brand-gray italic pl-1">{autoCalcHelperText}</p>
      )}

      {/* RH comment — visible only in validacao_rh for RH, or when existing content */}
      {showRhComment && (
        <Input
          className="text-sm"
          placeholder="Comentário RH..."
          value={dados.rhComentario ?? ''}
          disabled={!permissions.rhComentario}
          data-testid={`indicator-rh-comment-${indicador.id}`}
          onChange={(e) =>
            onUpdate(indicador.id, { rhComentario: e.target.value })
          }
        />
      )}

      {/* Rationale modal for auto-calculated indicators */}
      {rationale && (
        <Dialog open={showRationale} onOpenChange={setShowRationale}>
          <DialogContent className="bg-brand-dark border-brand-gray/20 shadow-monumenta-xl">
            <DialogHeader>
              <DialogTitle className="text-white font-display">{rationale.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {rationale.items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-brand-gray">{item.label}</span>
                  <span className="text-white font-medium">{item.value}</span>
                </div>
              ))}
            </div>
            {rationale.details && rationale.details.length > 0 && (
              <div className="mt-4 border-t border-brand-gray/20 pt-3">
                <h4 className="text-xs font-medium text-brand-gray mb-2">Detalhes</h4>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {rationale.details.map((d, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b border-brand-gray/5 last:border-0">
                      <span className="text-brand-gray">{d.label}</span>
                      <span className="text-white/80">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
